import { useState, useEffect, useCallback } from 'react';
import { api, type User } from '../shared/api-client';

function applyTheme(darkMode: boolean): void {
  // No cross-fade animation: the previous forced .theme-transition class
  // (a transition on every element plus a per-call 500ms removal timer)
  // raced with overlapping toggles and left backgrounds frozen mid-fade,
  // and made the canvas lag ~1s behind everything else. Flip instantly.
  document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  isDevOps: boolean;
  isProductEngineering: boolean;
  isSales: boolean;
  isSalesEngineering: boolean;
  isRfpKbManager: boolean;
}

export function useAuth(): AuthState & {
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  toggleDarkMode: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isAdmin: false,
    isDevOps: false,
    isProductEngineering: false,
    isSales: false,
    isSalesEngineering: false,
    isRfpKbManager: false,
  });

  const checkAuth = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const result = await api.getCurrentUser();

      if (result.success) {
        const isAdmin = result.data.groups.includes('admin');
        const isDevOps = result.data.groups.includes('devops');
        const isProductEngineering = result.data.groups.includes('product-engineering');
        const isSales = result.data.groups.includes('sales');
        const isSalesEngineering = result.data.groups.includes('sales-engineering');
        const routeGroups = result.data.routeGroups ?? [];
        const isRfpKbManager = isAdmin || routeGroups.includes('rfp-kb-managers');
        applyTheme(result.data.preferences?.darkMode ?? false);
        setState({ user: result.data, loading: false, error: null, isAdmin, isDevOps, isProductEngineering, isSales, isSalesEngineering, isRfpKbManager });
      } else {
        setState({ user: null, loading: false, error: null, isAdmin: false, isDevOps: false, isProductEngineering: false, isSales: false, isSalesEngineering: false, isRfpKbManager: false });
      }
    } catch {
      setState({ user: null, loading: false, error: 'Failed to check authentication', isAdmin: false, isDevOps: false, isProductEngineering: false, isSales: false, isSalesEngineering: false, isRfpKbManager: false });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
      setState({ user: null, loading: false, error: null, isAdmin: false, isDevOps: false, isProductEngineering: false, isSales: false, isSalesEngineering: false, isRfpKbManager: false });
      window.location.href = '/login';
    } catch {
      setState((prev) => ({ ...prev, error: 'Failed to logout', isSales: false, isSalesEngineering: false }));
    }
  }, []);

  const toggleDarkMode = useCallback(async () => {
    setState((prev) => {
      if (!prev.user) return prev;
      const newDarkMode = !(prev.user.preferences?.darkMode ?? false);
      const updatedUser: User = {
        ...prev.user,
        preferences: { ...prev.user.preferences, darkMode: newDarkMode },
      };
      applyTheme(newDarkMode);
      void api.updateUserPreferences(prev.user.id, { darkMode: newDarkMode });
      return { ...prev, user: updatedUser };
    });
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  return { ...state, logout, checkAuth, toggleDarkMode };
}
